import { useState, useEffect } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  FolderIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const PlanoContas = () => {
  const [planos, setPlanos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [manterFormularioAberto, setManterFormularioAberto] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      tipo: 'despesa',
      nivel: 1,
      codigo_contabil_integracao: ''
    }
  });

  useEffect(() => {
    carregarPlanos();
  }, []);

  const carregarPlanos = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/plano-contas');
      setPlanos(response.data);
    } catch (error) {
      console.error('Erro ao carregar plano de contas:', error);
      toast.error('Erro ao carregar plano de contas');
    } finally {
      setCarregando(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editingId) {
        await axios.put(`/api/plano-contas/${editingId}`, data);
        toast.success('Conta atualizada com sucesso!');
      } else {
        await axios.post('/api/plano-contas', data);
        toast.success('Conta cadastrada com sucesso!');
      }

      if (!editingId && manterFormularioAberto) {
        reset({
          codigo: '',
          descricao: '',
          tipo: data.tipo || 'despesa',
          nivel: data.nivel || 1,
          parent_id: data.parent_id || '',
          dre_grupo: data.dre_grupo || '',
          codigo_contabil_integracao: ''
        });
      } else {
        setShowModal(false);
        reset();
        setEditingId(null);
        setManterFormularioAberto(false);
      }

      if (editingId) {
        setEditingId(null);
      }
      carregarPlanos();
    } catch (error) {
      console.error('Erro ao salvar conta:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar conta');
    }
  };

  const handleEdit = (plano) => {
    reset(plano);
    setEditingId(plano.id);
    setManterFormularioAberto(false);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conta? Todas as subcontas também serão excluídas.')) {
      return;
    }

    try {
      await axios.delete(`/api/plano-contas/${id}`);
      toast.success('Conta excluída com sucesso!');
      carregarPlanos();
    } catch (error) {
      console.error('Erro ao excluir conta:', error);
      toast.error('Erro ao excluir conta');
    }
  };

  const getIconByTipo = (tipo) => {
    switch (tipo) {
      case 'receita':
        return <ArrowTrendingUpIcon className="h-5 w-5 text-success-600" />;
      case 'despesa':
        return <ArrowTrendingDownIcon className="h-5 w-5 text-danger-600" />;
      case 'patrimonio':
        return <ChartBarIcon className="h-5 w-5 text-primary-600" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getBadgeColorByTipo = (tipo) => {
    switch (tipo) {
      case 'receita':
        return 'badge-success';
      case 'despesa':
        return 'badge-danger';
      case 'patrimonio':
        return 'badge-info';
      default:
        return 'badge-info';
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'receita':
        return 'Receita';
      case 'despesa':
        return 'Despesa';
      case 'patrimonio':
        return 'Patrimônio';
      default:
        return tipo;
    }
  };

  // Filtrar planos
  const planosFiltrados = planos.filter(plano => {
    const matchesSearch = filtro === '' || 
      plano.codigo.toLowerCase().includes(filtro.toLowerCase()) ||
      plano.descricao.toLowerCase().includes(filtro.toLowerCase());
    
    const matchesTipo = filtroTipo === 'todos' || plano.tipo === filtroTipo;
    
    return matchesSearch && matchesTipo;
  });

  // Agrupar por tipo para estatísticas
  const stats = {
    receita: planos.filter(p => p.tipo === 'receita').length,
    despesa: planos.filter(p => p.tipo === 'despesa').length,
    patrimonio: planos.filter(p => p.tipo === 'patrimonio').length,
    total: planos.length
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📋 Plano de Contas</h1>
          <p className="text-gray-600">Estrutura contábil da empresa</p>
        </div>
        <button
          onClick={() => {
            reset();
            setEditingId(null);
            setManterFormularioAberto(false);
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Conta
        </button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <ArrowTrendingUpIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Contas de Receita</p>
                <p className="text-2xl font-bold text-gray-900">{stats.receita}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-danger-100">
                <ArrowTrendingDownIcon className="h-6 w-6 text-danger-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Contas de Despesa</p>
                <p className="text-2xl font-bold text-gray-900">{stats.despesa}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <ChartBarIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Contas de Patrimônio</p>
                <p className="text-2xl font-bold text-gray-900">{stats.patrimonio}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-gray-100">
                <FolderIcon className="h-6 w-6 text-gray-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Contas</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="form-group">
              <label className="form-label">
                <MagnifyingGlassIcon className="h-4 w-4 inline mr-1" />
                Buscar
              </label>
              <input
                type="text"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                className="form-input"
                placeholder="Código ou descrição..."
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Filtrar por Tipo</label>
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                className="form-select"
              >
                <option value="todos">Todos os tipos</option>
                <option value="receita">Receitas</option>
                <option value="despesa">Despesas</option>
                <option value="patrimonio">Patrimônio</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Ações</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setFiltro('');
                    setFiltroTipo('todos');
                  }}
                  className="btn-secondary flex-1"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Contas */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Estrutura de Contas ({planosFiltrados.length})
          </h3>
          {carregando && (
            <div className="loader h-6 w-6"></div>
          )}
        </div>
        
        <div className="card-body p-0">
          {planosFiltrados.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Descrição</th>
                    <th>Tipo</th>
                    <th>Nível</th>
                    <th>Grupo DRE</th>
                    <th>Conta Contábil</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {planosFiltrados.map((plano) => (
                    <tr key={plano.id}>
                      <td>
                        <div className="flex items-center">
                          {getIconByTipo(plano.tipo)}
                          <code className="ml-2 font-mono font-bold">{plano.codigo}</code>
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="font-medium">{plano.descricao}</p>
                          {plano.parent_id && (
                            <p className="text-xs text-gray-500">Subconta de {plano.parent_id}</p>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={getBadgeColorByTipo(plano.tipo)}>
                          {getTipoLabel(plano.tipo)}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center">
                          {Array.from({ length: plano.nivel }).map((_, i) => (
                            <FolderIcon key={i} className="h-4 w-4 text-gray-400" />
                          ))}
                          <span className="ml-1 text-sm">Nível {plano.nivel}</span>
                        </div>
                      </td>
                      <td>
                        <span className="text-sm text-gray-600">
                          {plano.dre_grupo || 'Não definido'}
                        </span>
                      </td>
                      <td>{plano.codigo_contabil_integracao || '-'}</td>
                      <td>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(plano)}
                            className="text-primary-600 hover:text-primary-900 p-1"
                            title="Editar"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(plano.id)}
                            className="text-danger-600 hover:text-danger-900 p-1"
                            title="Excluir"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-lg font-medium">Nenhuma conta encontrada</p>
              <p className="text-sm mt-1">
                {filtro || filtroTipo !== 'todos' 
                  ? 'Nenhum resultado para os filtros aplicados' 
                  : 'Cadastre sua primeira conta no plano de contas'}
              </p>
              {(filtro || filtroTipo !== 'todos') ? (
                <button
                  onClick={() => {
                    setFiltro('');
                    setFiltroTipo('todos');
                  }}
                  className="btn-primary mt-4"
                >
                  Limpar Filtros
                </button>
              ) : (
                <button
                  onClick={() => setShowModal(true)}
                  className="btn-primary mt-4"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Cadastrar Primeira Conta
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Conta */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingId ? '✏️ Editar Conta' : '➕ Nova Conta'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">Código *</label>
                  <input
                    type="text"
                    {...register('codigo', { 
                      required: 'Código é obrigatório',
                      pattern: {
                        value: /^[0-9.]+$/,
                        message: 'Código deve conter apenas números e pontos'
                      }
                    })}
                    className="form-input"
                    placeholder="Ex: 1.01, 2.01.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use pontos para hierarquia (Ex: 1.01.001)
                  </p>
                  {errors.codigo && (
                    <p className="form-error">{errors.codigo.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Descrição *</label>
                  <input
                    type="text"
                    {...register('descricao', { 
                      required: 'Descrição é obrigatória',
                      minLength: {
                        value: 3,
                        message: 'Mínimo 3 caracteres'
                      }
                    })}
                    className="form-input"
                    placeholder="Ex: Venda de Produtos, Salários"
                  />
                  {errors.descricao && (
                    <p className="form-error">{errors.descricao.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Tipo *</label>
                  <select
                    {...register('tipo', { required: 'Tipo é obrigatório' })}
                    className="form-select"
                  >
                    <option value="">Selecione...</option>
                    <option value="receita">Receita</option>
                    <option value="despesa">Despesa</option>
                    <option value="patrimonio">Patrimônio</option>
                  </select>
                  {errors.tipo && (
                    <p className="form-error">{errors.tipo.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Nível</label>
                    <select
                      {...register('nivel')}
                      className="form-select"
                    >
                      <option value="1">1 - Principal</option>
                      <option value="2">2 - Subconta</option>
                      <option value="3">3 - Detalhe</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Conta Pai</label>
                    <select
                      {...register('parent_id')}
                      className="form-select"
                    >
                      <option value="">Nenhuma (conta principal)</option>
                      {planos
                        .filter(p => p.nivel < 3)
                        .map(plano => (
                          <option key={plano.id} value={plano.id}>
                            {plano.codigo} - {plano.descricao}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Grupo DRE</label>
                  <select
                    {...register('dre_grupo')}
                    className="form-select"
                  >
                    <option value="">Não categorizado</option>
                    <option value="Receita Operacional">Receita Operacional</option>
                    <option value="Receita Não Operacional">Receita Não Operacional</option>
                    <option value="Deduções">Deduções</option>
                    <option value="Custos">Custos</option>
                    <option value="Despesas Operacionais">Despesas Operacionais</option>
                    <option value="Despesas Não Operacionais">Despesas Não Operacionais</option>
                    <option value="Despesas Tributárias">Despesas Tributárias</option>
                    <option value="Despesas Financeiras">Despesas Financeiras</option>
                    <option value="Resultado">Resultado</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Conta Contabil de Integracao</label>
                  <input
                    type="text"
                    {...register('codigo_contabil_integracao')}
                    className="form-input"
                    placeholder="Codigo no sistema contabil"
                    maxLength={30}
                  />
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end space-x-3">
                {!editingId && (
                  <label className="mr-auto inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={manterFormularioAberto}
                      onChange={(e) => setManterFormularioAberto(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Continuar incluindo apos salvar
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    reset();
                    setEditingId(null);
                    setManterFormularioAberto(false);
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingId ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanoContas;
